import React, { useCallback, useMemo } from "react";
import {
  Box,
  Cards,
  ContentLayout,
  Header,
  Link,
} from "@amzn/awsui-components-console";
import { useNavigate } from "react-router-dom";

import { buildLobClaimIntakePath } from "src/jsonForms/constants";

import { LOB_LABELS, LOB_DESCRIPTIONS, LOB_ORDER } from "./lobAccess";
import type { LobKey } from "./types";

interface LobSelectorProps {
  /**
   * The LOBs the user has access to. The selector renders one card per
   * available LOB in `LOB_ORDER` (wc → cargo → ipl) and omits any LOB
   * the user cannot access.
   */
  available: LobKey[];
}

interface LobCardItem {
  lob: LobKey;
  label: string;
  description: string;
}

/**
 * LOB selector rendered by `JsonFormsClaimIntakeApp` when the user has
 * access to more than one LOB and no `<lob>` URL parameter is present.
 *
 * Wraps the `Cards` component in `ContentLayout` so the page heading
 * sits in the layout's standard header slot rather than floating inside
 * a card container. Selecting a card link navigates to that LOB's
 * pre-screen route via React Router (no full page reload).
 *
 * The component preserves the `lob-selector` and `lob-selector-<lob>`
 * data-testids used by `JsonFormsClaimIntakeApp.test.tsx` so the
 * orchestrator-level tests continue to pass.
 */
function LobSelector({ available }: LobSelectorProps): JSX.Element {
  const navigate = useNavigate();

  const items = useMemo<LobCardItem[]>(
    () =>
      LOB_ORDER.filter((lob) => available.includes(lob)).map((lob) => ({
        lob,
        label: LOB_LABELS[lob],
        description: LOB_DESCRIPTIONS[lob],
      })),
    [available]
  );

  const handleSelect = useCallback(
    (lob: LobKey) => {
      navigate(buildLobClaimIntakePath(lob));
    },
    [navigate]
  );

  return (
    <div data-testid="lob-selector">
      <ContentLayout
        header={
          <Header
            variant="h1"
            description="Choose the type of claim you want to submit. You can come back to this page anytime to switch claim types."
          >
            Select a claim type to get started
          </Header>
        }
      >
        <Cards<LobCardItem>
          items={items}
          trackBy="lob"
          variant="container"
          cardsPerRow={[{ cards: 1 }, { minWidth: 768, cards: 2 }]}
          cardDefinition={{
            header: (item) => (
              <span data-testid={`lob-selector-${item.lob}`}>
                <Link
                  onFollow={(event) => {
                    event.preventDefault();
                    handleSelect(item.lob);
                  }}
                  href={buildLobClaimIntakePath(item.lob)}
                  fontSize="heading-m"
                >
                  {item.label}
                </Link>
              </span>
            ),
            sections: [
              {
                id: "description",
                content: (item) => <Box variant="p">{item.description}</Box>,
              },
            ],
          }}
        />
      </ContentLayout>
    </div>
  );
}

export default LobSelector;
